'use client';

import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type { Drone } from '@/types/fleet';
import type { ManualControlSnapshot } from '@/types/coordination';

/**
 * What `GET /api/drones/:id` says about a live manual session on the SELECTED
 * aircraft — the one thing the COP cannot learn from anywhere else.
 *
 * The list route (`useDroneRegistry`) does not carry `manual_control`, and the
 * org-wide `manual_control_state` broadcast carries a session id this console
 * has no memory of after a reload. So a COP that reloads mid-session reads its
 * OWN session back as a foreign hold: `manualHoldRefusal` names the signed-in
 * operator, the MANUAL key goes dead, and the operator is locked out of their
 * own aircraft until the lock's TTL expires. The detail route was extended for
 * exactly this and had no consumer; this hook is it.
 *
 * One request per selection, deliberately. Live changes after that arrive on the
 * WebSocket — this is the answer to "what was already true when this screen
 * opened", and re-polling it would be a second source of truth for something the
 * broadcast already reports at 1 Hz.
 *
 * Failure degrades to null rather than being surfaced: the fallout is that a
 * reload does not resume, which is the behaviour that existed before this
 * endpoint, and it must never take the drone panel down with it.
 */
export function useManualControlSnapshot(droneId: string | null): ManualControlSnapshot | null {
  // Stored WITH the aircraft it was read for, and matched during render rather
  // than cleared in an effect. A snapshot belongs to one airframe: returning the
  // previous selection's for even one commit would offer the controller a
  // session on an aircraft the operator is no longer looking at.
  const [entry, setEntry] = useState<{ droneId: string; snapshot: ManualControlSnapshot | null } | null>(
    null,
  );

  useEffect(() => {
    if (!droneId) return;
    let cancelled = false;
    api
      .get<{ data: Drone & { manual_control?: ManualControlSnapshot | null } }>(
        `/api/drones/${droneId}`,
      )
      .then((r) => {
        if (cancelled) return;
        setEntry({ droneId, snapshot: r.data.manual_control ?? null });
      })
      .catch(() => {
        if (cancelled) return;
        setEntry({ droneId, snapshot: null });
        console.warn('Could not read manual control state for the selected aircraft');
      });
    return () => {
      cancelled = true;
    };
  }, [droneId]);

  return entry !== null && entry.droneId === droneId ? entry.snapshot : null;
}
